import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { UserRepository, UserListOptions, UserListResponse } from '../../domain/repositories/UserRepository';
import { User } from '../../domain/entities/User';
import { RepositoryError } from '../../domain/errors';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext } from '../../domain/value-objects/UserContext';
import { BaseFirebaseRepository, FilterCondition } from './BaseFirebaseRepository';

export class FirebaseUserRepository extends BaseFirebaseRepository<User> implements UserRepository {
  constructor(logger: ILoggerService) {
    super('users', logger);
  }

  protected convertFirestoreToEntity(id: string, data: any): User {
    return User.create(
      id,
      data.email,
      data.name,
      data.organizationId,
      data.organizationName,
      data.role,
      data.locationIds || [],
      data.cguAccepted || false,
      data.cguAcceptedAt ? this.convertFirestoreDateNullable(data.cguAcceptedAt) : null,
      this.convertFirestoreDate(data.createdAt),
      this.convertFirestoreDate(data.updatedAt)
    );
  }

  protected getDefaultSortField(): string {
    return 'email';
  }

  protected mapSortField(sortBy: string): string {
    switch (sortBy) {
      case 'name':
        return 'name';
      case 'organizationName':
        return 'organizationName';
      case 'email':
      default:
        return 'email';
    }
  }

  protected applyLocationScopeFilters(_locationIds: string[]): FilterCondition[] {
    return [];
  }

  protected applySearchFilter(users: User[], search: string): User[] {
    const searchLower = search.toLowerCase();
    return users.filter(user =>
      user.email.toLowerCase().includes(searchLower) ||
      user.name.toLowerCase().includes(searchLower) ||
      user.organizationName.toLowerCase().includes(searchLower)
    );
  }

  async findAll(options: UserListOptions = {}, userContext?: UserContext): Promise<UserListResponse> {
    const {
      organizationId,
      ...queryOptions
    } = options;

    const response = await this.executeQuery(userContext, queryOptions, [], [], organizationId);
    
    return {
      users: response.items,
      totalCount: response.totalCount,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
      pageSize: response.pageSize
    };
  }

  async countWithUserContext(userContext?: UserContext): Promise<number> {
    return this.countWithFilters(userContext);
  }

  async getUser(uid: string): Promise<User> {
    const user = await this.findById(uid);
    if (!user) {
      throw RepositoryError.notFound();
    }
    return user;
  }



  async updateCGUAcceptanceUser(user: User): Promise<User> {
    try {
      const userDocRef = doc(db, 'users', user.uid);
      
      const updateData: any = {
        updatedAt: Timestamp.now(),
        cguAccepted: user.cguAccepted
      };

      // Only set cguAcceptedAt if the user has accepted CGU
      if (user.cguAccepted && user.cguAcceptedAt) {
        updateData.cguAcceptedAt = Timestamp.fromDate(user.cguAcceptedAt);
      }

      await updateDoc(userDocRef, updateData);
      
      // Return the updated user
      return user;
    } catch (error) {
      this.logger.error('Error updating user CGU acceptance:', error);
      throw RepositoryError.queryFailed();
    }
  }
}